import { defineComponent as _defineComponent } from 'vue'
import FormLayout from '@/components/common/FormLayout.vue'
import { BatchPaymentTerms, PAYMENT_TERMS_OPTIONS } from '@/models/invoice'
import moment from 'moment/moment'
import { computed, reactive, ref, watchEffect } from 'vue'
import { useInvoiceQuery, useUpdateInvoiceMutation } from '@/api/invoice'
import { getDueDate } from '@/utils/invoice-due-date'
import VsAutocomplete from '@/components/vision/Inputs/VsAutocomplete/VsAutocomplete.vue'
import VsForm from '@/components/vision/VsForm.vue'
import VsTextInput from '@/components/vision/Inputs/VsTextInput.vue'
import VsDatePicker from '@/components/vision/Inputs/VsDatePicker.vue'
import VsButton from '@/components/vision/VsButton.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'

interface FormData {
  id?: string | null
  batchNumber?: string | null
  batchDate?: moment.Moment | null
  paymentTerms?: BatchPaymentTerms | null
  dueDate?: moment.Moment | null
  memo?: string | null
}


export default /*#__PURE__*/_defineComponent({
  __name: 'BatchEditForm',
  props: {
    id: null
  },
  emits: ["dismiss"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'dismiss')
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  id: string
}

const ID_NEW = 'new'

const formData = reactive<FormData>({
  id: undefined,
  batchNumber: undefined,
  batchDate: undefined,
  paymentTerms: undefined,
  dueDate: undefined,
  memo: undefined,
})


const idRef = computed(() => props.id)
const { data: batch, isLoading: isBatchLoading } = useInvoiceQuery(idRef)

const {
  mutateAsync: updateInvoice,
  isLoading: saving,
  error,
} = useUpdateInvoiceMutation()

const errorMessage = computed(() => {
  if (error.value && error.value?.response?.status === 409) {
    return `Batch number "${formData.batchNumber}" already exists`
  } else {
    return error.value
  }
})

const snackbarMessage = ref<string | null>(null)
const showSnackbarError = ref<boolean>(false)

const hasChanges = computed(() => {
  if (isBatchLoading.value || !batch.value) {
    return false
  } else {
    return (
      formData.batchNumber !== batch.value?.invoiceNumber ||
      !areDatesEqual(
        formData.batchDate,
        batch.value ? moment.utc(batch.value.invoiceDate) : null
      ) ||
      formData.paymentTerms !== batch.value.paymentTerms ||
      !areDatesEqual(
        formData.dueDate,
        batch.value && batch.value.dueDate
          ? moment.utc(batch.value?.dueDate)
          : null
      ) ||
      formData.memo !== batch.value.memo
    )
  }
})

function areDatesEqual(
  date1: moment.Moment | null | undefined,
  date2: moment.Moment | null | undefined
) {
  if (!date1 && !date2) {
    return true
  }
  if (date1 && date2) {
    return date1.isSame(date2)
  }
  return false
}

watchEffect(() => {
  if (props.id !== ID_NEW && !isBatchLoading.value && batch.value) {
    formData.id = props.id
    formData.batchNumber = batch.value.invoiceNumber
    formData.batchDate = moment.utc(batch.value.invoiceDate).local()
    formData.paymentTerms = batch.value.paymentTerms
    formData.dueDate = batch.value.dueDate
      ? moment.utc(batch.value.dueDate).local()
      : null
    formData.memo = batch.value.memo
  }
})

const invoiceForm = ref()


function setInvoiceNumber(batchNumber: string) {
  formData.batchNumber = batchNumber
}

function setInvoiceDate(batchDate: moment.Moment) {
  formData.batchDate = batchDate
  if (formData.paymentTerms) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  }
}

function setDueDate(dueDate: moment.Moment) {
  formData.dueDate = dueDate
}

function setPaymentTerms(newPaymentTerms: BatchPaymentTerms | null) {
  formData.paymentTerms = newPaymentTerms
  if (formData.paymentTerms && formData.batchDate) {
    formData.dueDate = getDueDate(formData.batchDate, formData.paymentTerms)
  }
}

function setMemo(newMemo: string | null) {
  formData.memo = newMemo
}

async function save() {
  if (!invoiceForm.value.isValid()) return
  try {
    if (!batch.value) return
    await updateInvoice({
      id: batch.value?.id,
      invoiceNumber: formData.batchNumber,
      invoiceDate: formData.batchDate?.utc(),
      paymentTerms: formData.paymentTerms ?? BatchPaymentTerms.DueOnReceipt,
      dueDate: formData.dueDate?.utc(),
      jobIds: undefined,
      memo: formData.memo,
    })
    snackbarMessage.value = 'Batch saved'
    dismiss()
  } catch (e) {
    showSnackbarError.value = true
  }
}

function clearSnackbarMessage() {
  snackbarMessage.value = null
}

function clearSnackbarError() {
  showSnackbarError.value = false
}

function dismiss() {
  emit('dismiss')
}

return { __sfc: true,ID_NEW, formData, props, idRef, batch, isBatchLoading, updateInvoice, saving, error, errorMessage, snackbarMessage, showSnackbarError, hasChanges, areDatesEqual, invoiceForm, emit, setInvoiceNumber, setInvoiceDate, setDueDate, setPaymentTerms, setMemo, save, clearSnackbarMessage, clearSnackbarError, dismiss, FormLayout, BatchPaymentTerms, PAYMENT_TERMS_OPTIONS, VsAutocomplete, VsForm, VsTextInput, VsDatePicker, VsButton, VsSnackbar }
}

})